/**
 * The offline system of record (D-10, D-17, `TRACK-D-WORKER-PWA.md`).
 *
 * IndexedDB is the record and Redux is a mirror, in that order and never the
 * reverse: a dispatch that succeeds while the write fails shows a worker a
 * punch that does not exist, and a punch that exists only in memory is a wage
 * dispute (I-7).
 *
 * ## One database, one set of names, one version
 *
 * The queue used to keep a database of its own because `@saniplus/api-client`
 * opened `saniplus-worker` at a pinned version 1 and adding stores to it would
 * have thrown `VersionError` on every later `getOrCreateDeviceUuid`. That is
 * settled: the api-client's own `upgrade` now creates these four stores
 * alongside `meta`, and the names and the version come from there rather than
 * being re-declared and hand-synced — a second copy of a version number is
 * how the next bump meets a `VersionError` in production.
 *
 * The raw IndexedDB API is used rather than `idb` because `idb` is a
 * dependency of `@saniplus/api-client`, not of this app, and adding a
 * dependency requires an install this agent must not run. The
 * `onversionchange` handler below is this file's `blocking()`: it holds a
 * long-lived connection, so an upgrade started anywhere else — another tab,
 * or the api-client after a `META_DB_VERSION` bump — would stall on it
 * forever if it did not step aside.
 */
import {
  CLOCK_ANCHOR_STORE,
  CONTEXT_CACHE_STORE,
  FAILED_PUNCHES_STORE,
  META_DB_VERSION,
  PUNCH_QUEUE_STORE,
} from '@saniplus/api-client';
import type { ErrorCode, Gps, GpsStatus, MeContextResponse } from '@saniplus/contracts';

import { IDB_NAME } from '../../app/config';

export const OFFLINE_DB_NAME = IDB_NAME;
export const OFFLINE_DB_VERSION = META_DB_VERSION;

export { CLOCK_ANCHOR_STORE, CONTEXT_CACHE_STORE, FAILED_PUNCHES_STORE, PUNCH_QUEUE_STORE };

const SINGLETON_KEY = 'current';

/** Lower-case on the client (TRACK-D); the wire enum is `IN` / `OUT`. */
export type QueuedPunchType = 'in' | 'out';

/** `sending` is the single-flight marker: one submitter owns the row. */
export type QueuedPunchState = 'pending' | 'sending';

/** `ok` is a server-side outcome; the client only ever reports a failure. */
export type MissingGpsStatus = Exclude<GpsStatus, 'ok'>;

/** D-10's raw timing evidence, exactly as `CapturedAtSchema` accepts it. */
export interface QueuedCapturedAt {
  readonly deviceRecordedAt: string;
  /**
   * Absent after a cold start: `performance.now()` restarted with the JS
   * context, so the monotonic chain is broken. Omitting it *is* the mark on
   * the payload — the server then falls back to `deviceRecordedAt` and always
   * raises `CLOCK_DRIFT` (the amendment on `CapturedAtSchema`).
   */
  readonly monotonicElapsedMs?: number;
  readonly clockOffsetMs: number;
  readonly lastServerContactAt: string;
}

export interface QueuedPunch {
  readonly clientPunchUuid: string;
  /**
   * The user this punch belongs to, stamped from the auth slice at enqueue.
   *
   * A phone is handed around a crew: worker A punches OUT with no signal, B
   * logs in on the same device, and the next flush would send A's row under
   * B's bearer token — the server resolves the user from the token, so A's
   * punch lands on B's payslip. Every read below is therefore owner-scoped and
   * a row whose owner is not the current session is never listed, never sent
   * and never counted. It is also never deleted: it stays here under A until A
   * signs back in on this device (I-7 — a punch is never dropped).
   *
   * A row without an owner predates this field. It is excluded from every
   * session for the same reason: there is nobody it can be safely sent as.
   */
  readonly ownerUserId: string;
  readonly type: QueuedPunchType;
  /**
   * Required by the API for `in`. Carried for `out` too — locally only, never
   * sent — because the "En attente d'envoi" rule matches a queued OUT to a
   * shift the server still reports as `missing_punch_out`.
   */
  readonly shiftId?: string;
  readonly deviceUuid: string;
  readonly capturedAt?: QueuedCapturedAt;
  readonly gps?: Gps;
  readonly gpsStatus?: MissingGpsStatus;
  readonly attempts: number;
  readonly lastError?: string;
  readonly state: QueuedPunchState;
  /** Device clock at enqueue time; the tiebreak for capture-time ordering. */
  readonly queuedAt: string;
  /** True when `capturedAt` was built from a restored (cold-start) anchor. */
  readonly anchorReconstructed: boolean;
}

export interface FailedPunch extends QueuedPunch {
  readonly refusalCode: ErrorCode;
  readonly refusedAt: string;
  readonly details?: unknown;
  /** Set when the worker has actually seen it. The row is never deleted. */
  readonly acknowledgedAt?: string;
}

export interface PersistedClockAnchor {
  readonly offsetMs: number;
  readonly monotonicBaseMs: number;
  readonly wallClockBaseMs: number;
  readonly lastServerContactAt: string;
}

export interface CachedContext {
  readonly payload: MeContextResponse;
  readonly cachedAt: string;
}

type Keyed<T> = T & { readonly key: string };

/**
 * `ownerUserId` is optional only on the way *out*: a row written before the
 * field existed has none, and reads as another worker's — which is exactly
 * what it is (H-2, same contract as `QueuedPunch.ownerUserId`).
 */
type StoredContext = Keyed<CachedContext> & { readonly ownerUserId?: string };

let dbPromise: Promise<IDBDatabase> | null = null;

function requestFailed(error: DOMException | null, what: string): Error {
  return new Error(`${what}: ${error?.message ?? 'IndexedDB request failed'}`);
}

function awaitRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = (): void => {
      resolve(request.result);
    };
    request.onerror = (): void => {
      reject(requestFailed(request.error, 'offline store request'));
    };
  });
}

function awaitTransaction(transaction: IDBTransaction): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    transaction.oncomplete = (): void => {
      resolve();
    };
    transaction.onerror = (): void => {
      reject(requestFailed(transaction.error, 'offline store transaction'));
    };
    transaction.onabort = (): void => {
      reject(requestFailed(transaction.error, 'offline store transaction aborted'));
    };
  });
}

export function openOfflineDb(): Promise<IDBDatabase> {
  dbPromise ??= new Promise<IDBDatabase>((resolve, reject) => {
    const open = indexedDB.open(OFFLINE_DB_NAME, OFFLINE_DB_VERSION);
    open.onupgradeneeded = (): void => {
      const db = open.result;
      if (!db.objectStoreNames.contains(PUNCH_QUEUE_STORE)) {
        db.createObjectStore(PUNCH_QUEUE_STORE, { keyPath: 'clientPunchUuid' });
      }
      if (!db.objectStoreNames.contains(FAILED_PUNCHES_STORE)) {
        db.createObjectStore(FAILED_PUNCHES_STORE, { keyPath: 'clientPunchUuid' });
      }
      if (!db.objectStoreNames.contains(CLOCK_ANCHOR_STORE)) {
        db.createObjectStore(CLOCK_ANCHOR_STORE, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(CONTEXT_CACHE_STORE)) {
        db.createObjectStore(CONTEXT_CACHE_STORE, { keyPath: 'key' });
      }
    };
    open.onsuccess = (): void => {
      const db = open.result;
      // Another tab upgrading must not leave this connection blocking it.
      db.onversionchange = (): void => {
        db.close();
        dbPromise = null;
      };
      resolve(db);
    };
    open.onerror = (): void => {
      reject(requestFailed(open.error, 'opening the offline database'));
    };
  }).catch((cause: unknown) => {
    dbPromise = null;
    throw cause instanceof Error ? cause : new Error('opening the offline database failed');
  });
  return dbPromise;
}

/**
 * One transaction. `run` must issue its requests synchronously (or from
 * inside a previous request's continuation), which is what keeps the
 * transaction alive — the same discipline `idb` follows.
 */
async function runTx<T>(
  stores: string[],
  mode: IDBTransactionMode,
  run: (transaction: IDBTransaction) => Promise<T>,
): Promise<T> {
  const db = await openOfflineDb();
  const transaction = db.transaction(stores, mode);
  const settled = awaitTransaction(transaction);
  const result = await run(transaction);
  await settled;
  return result;
}

/**
 * D-10: `deviceRecordedAt` is evidence, not an ordering key. An OS clock
 * correction mid-shift moves it backwards while real time keeps going
 * forward, and an OUT that sorts ahead of its own IN inside one batch is a
 * payroll defect the server cannot undo. `clockOffsetMs` is what the server
 * said this device was wrong by at capture time, so adding it back puts every
 * row on one clock — the server's. The stored evidence is left untouched.
 */
function captureOrder(record: QueuedPunch): number {
  const captured = record.capturedAt;
  if (captured !== undefined) {
    const parsed = Date.parse(captured.deviceRecordedAt);
    if (!Number.isNaN(parsed)) return parsed + captured.clockOffsetMs;
  }
  // No `capturedAt` means this device has never seen a server time, so there
  // is no offset to apply — `queuedAt` is the best reading that exists.
  return Date.parse(record.queuedAt);
}

/** D-10: the batch is processed in capture-time order, so it is sent that way. */
function sortByCaptureTime(records: QueuedPunch[]): QueuedPunch[] {
  return [...records].sort((a, b) => captureOrder(a) - captureOrder(b));
}

/** A row belongs to the session only when the owners match exactly. */
function isOwnedBy(record: QueuedPunch, ownerUserId: string): boolean {
  return record.ownerUserId === ownerUserId;
}

export function putQueuedPunch(record: QueuedPunch): Promise<void> {
  return runTx([PUNCH_QUEUE_STORE], 'readwrite', async (transaction) => {
    await awaitRequest(transaction.objectStore(PUNCH_QUEUE_STORE).put(record));
  });
}

/**
 * `ownerUserId` is required, not optional: an accidental omission would read
 * another worker's row, which is the whole failure this field exists to stop.
 */
export function getQueuedPunch(
  clientPunchUuid: string,
  ownerUserId: string,
): Promise<QueuedPunch | null> {
  return runTx([PUNCH_QUEUE_STORE], 'readonly', async (transaction) => {
    const found = await awaitRequest<QueuedPunch | undefined>(
      transaction.objectStore(PUNCH_QUEUE_STORE).get(clientPunchUuid) as IDBRequest<
        QueuedPunch | undefined
      >,
    );
    if (found === undefined) return null;
    return isOwnedBy(found, ownerUserId) ? found : null;
  });
}

export async function listQueuedPunches(ownerUserId: string): Promise<QueuedPunch[]> {
  const all = await runTx([PUNCH_QUEUE_STORE], 'readonly', async (transaction) =>
    awaitRequest<QueuedPunch[]>(
      transaction.objectStore(PUNCH_QUEUE_STORE).getAll() as IDBRequest<QueuedPunch[]>,
    ),
  );
  return sortByCaptureTime(all.filter((record) => isOwnedBy(record, ownerUserId)));
}

/**
 * Every row, owner included. Only the sign-out check uses it — "does anything
 * at all still need sending before this session ends" — never a submit path.
 */
export function listAllQueuedPunches(): Promise<QueuedPunch[]> {
  return runTx([PUNCH_QUEUE_STORE], 'readonly', async (transaction) =>
    awaitRequest<QueuedPunch[]>(
      transaction.objectStore(PUNCH_QUEUE_STORE).getAll() as IDBRequest<QueuedPunch[]>,
    ),
  );
}

export function deleteQueuedPunch(clientPunchUuid: string): Promise<void> {
  return runTx([PUNCH_QUEUE_STORE], 'readwrite', async (transaction) => {
    await awaitRequest(transaction.objectStore(PUNCH_QUEUE_STORE).delete(clientPunchUuid));
  });
}

function withoutGps(record: QueuedPunch): QueuedPunch {
  const copy: Record<string, unknown> = { ...record };
  delete copy['gps'];
  delete copy['gpsStatus'];
  return copy as unknown as QueuedPunch;
}

/** `ownerUserId` is deliberately absent: a row never changes hands. */
type QueuedPunchPatch = Partial<
  Pick<QueuedPunch, 'attempts' | 'lastError' | 'state' | 'gps' | 'gpsStatus'>
>;

/**
 * Read-modify-write inside one transaction: two submitters racing on the same
 * row must not lose one another's `attempts`.
 */
export function patchQueuedPunch(
  clientPunchUuid: string,
  patch: QueuedPunchPatch,
): Promise<QueuedPunch | null> {
  return runTx([PUNCH_QUEUE_STORE], 'readwrite', async (transaction) => {
    const store = transaction.objectStore(PUNCH_QUEUE_STORE);
    const existing = await awaitRequest<QueuedPunch | undefined>(
      store.get(clientPunchUuid) as IDBRequest<QueuedPunch | undefined>,
    );
    if (existing === undefined) return null;
    // `gps` XOR `gpsStatus`: patching one always clears the other, or the
    // late GPS fix would ride alongside the placeholder failure status.
    const cleared =
      patch.gps !== undefined || patch.gpsStatus !== undefined ? withoutGps(existing) : existing;
    const next: QueuedPunch = { ...cleared, ...patch, ownerUserId: existing.ownerUserId };
    await awaitRequest(store.put(next));
    return next;
  });
}

/**
 * I-7: a refused punch is moved, never dropped. One transaction over both
 * stores, so it can never be absent from both.
 */
export function moveToFailed(
  clientPunchUuid: string,
  refusal: { refusalCode: ErrorCode; refusedAt: string; details?: unknown },
): Promise<FailedPunch | null> {
  return runTx([PUNCH_QUEUE_STORE, FAILED_PUNCHES_STORE], 'readwrite', async (transaction) => {
    const queue = transaction.objectStore(PUNCH_QUEUE_STORE);
    const failed = transaction.objectStore(FAILED_PUNCHES_STORE);
    const existing = await awaitRequest<QueuedPunch | undefined>(
      queue.get(clientPunchUuid) as IDBRequest<QueuedPunch | undefined>,
    );
    if (existing === undefined) return null;
    // L-5: the coordinates can never be sent anywhere once the punch is
    // refused, so they are pure retention liability on a shared plant phone
    // (Law 25, R-04). The row itself still moves rather than dies (I-7).
    const record: FailedPunch = {
      ...withoutGps(existing),
      state: 'pending',
      refusalCode: refusal.refusalCode,
      refusedAt: refusal.refusedAt,
      ...(refusal.details === undefined ? {} : { details: refusal.details }),
    };
    await awaitRequest(failed.put(record));
    await awaitRequest(queue.delete(clientPunchUuid));
    return record;
  });
}

export async function listFailedPunches(ownerUserId: string): Promise<FailedPunch[]> {
  const all = await runTx([FAILED_PUNCHES_STORE], 'readonly', async (transaction) =>
    awaitRequest<FailedPunch[]>(
      transaction.objectStore(FAILED_PUNCHES_STORE).getAll() as IDBRequest<FailedPunch[]>,
    ),
  );
  return all
    .filter((record) => isOwnedBy(record, ownerUserId))
    .sort((a, b) => Date.parse(a.refusedAt) - Date.parse(b.refusedAt));
}

/** The worker has seen it. The row stays — nothing is ever deleted silently. */
export function acknowledgeFailedPunch(
  clientPunchUuid: string,
  acknowledgedAt: string,
): Promise<FailedPunch | null> {
  return runTx([FAILED_PUNCHES_STORE], 'readwrite', async (transaction) => {
    const store = transaction.objectStore(FAILED_PUNCHES_STORE);
    const existing = await awaitRequest<FailedPunch | undefined>(
      store.get(clientPunchUuid) as IDBRequest<FailedPunch | undefined>,
    );
    if (existing === undefined) return null;
    const next: FailedPunch = { ...existing, acknowledgedAt };
    await awaitRequest(store.put(next));
    return next;
  });
}

export function readClockAnchor(): Promise<PersistedClockAnchor | null> {
  return runTx([CLOCK_ANCHOR_STORE], 'readonly', async (transaction) => {
    const found = await awaitRequest<Keyed<PersistedClockAnchor> | undefined>(
      transaction.objectStore(CLOCK_ANCHOR_STORE).get(SINGLETON_KEY) as IDBRequest<
        Keyed<PersistedClockAnchor> | undefined
      >,
    );
    if (found === undefined) return null;
    return {
      offsetMs: found.offsetMs,
      monotonicBaseMs: found.monotonicBaseMs,
      wallClockBaseMs: found.wallClockBaseMs,
      lastServerContactAt: found.lastServerContactAt,
    };
  });
}

export function writeClockAnchor(anchor: PersistedClockAnchor): Promise<void> {
  return runTx([CLOCK_ANCHOR_STORE], 'readwrite', async (transaction) => {
    await awaitRequest(
      transaction.objectStore(CLOCK_ANCHOR_STORE).put({ key: SINGLETON_KEY, ...anchor }),
    );
  });
}

/**
 * The cache is a singleton row on a phone a crew hands around: worker A's
 * shift, site address and supervisor phone numbers must not be what worker B
 * reads offline. `ownerUserId` is required for the same reason it is on
 * `getQueuedPunch` — an omission would read someone else's row.
 */
export function readCachedContext(ownerUserId: string): Promise<CachedContext | null> {
  return runTx([CONTEXT_CACHE_STORE], 'readonly', async (transaction) => {
    const found = await awaitRequest<StoredContext | undefined>(
      transaction.objectStore(CONTEXT_CACHE_STORE).get(SINGLETON_KEY) as IDBRequest<
        StoredContext | undefined
      >,
    );
    if (found?.ownerUserId !== ownerUserId) return null;
    return { payload: found.payload, cachedAt: found.cachedAt };
  });
}

export function writeCachedContext(
  payload: MeContextResponse,
  cachedAt: string,
  ownerUserId: string,
): Promise<void> {
  return runTx([CONTEXT_CACHE_STORE], 'readwrite', async (transaction) => {
    const record: StoredContext = { key: SINGLETON_KEY, payload, cachedAt, ownerUserId };
    await awaitRequest(transaction.objectStore(CONTEXT_CACHE_STORE).put(record));
  });
}

/** Test helper: drop every store's contents without deleting the database. */
export async function clearOfflineDb(): Promise<void> {
  const stores = [PUNCH_QUEUE_STORE, FAILED_PUNCHES_STORE, CLOCK_ANCHOR_STORE, CONTEXT_CACHE_STORE];
  await runTx(stores, 'readwrite', async (transaction) => {
    await Promise.all(stores.map((name) => awaitRequest(transaction.objectStore(name).clear())));
  });
}
